import Column from '@components/column'
import { useEffect, useState, useRef, FC } from 'react'
import { Formik, Form } from 'formik'
import { useSelector } from 'react-redux'
import Fields from '@components/fields'
import { Medicine, Category } from '@apis'
import Circle from '@components/circle'
import Typography from '@components/typography'
import debounce from '@utils/debounce'
import {
  StyleSearch,
  StyleInputWrapper,
  StyleInput,
  StyleWrapper,
  StyleSearchWrapper,
  StyleSearchItem,
} from './style'
import Svg from '@components/svg'
import Empty from '@commons/empty'
import Button from '@components/button'
import Row from '@components/row'
import Modal from '@components/modal'
import { MedicineSchema } from '@validations'
interface SearchMedicinesProps {
  selectedItems: any[]
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
}

const initial = {
  name: '',
  duration: '',
  consumptions: [],
  notes: '',
}
const SearchMedicines: FC<SearchMedicinesProps> = ({
  selectedItems,
  setFieldValue,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState<boolean>(false)
  const [medicineVisible, setMedicineVisible] = useState<boolean>(false)
  const { data: medicines } = useSelector((state: any) => state.searchMedicines)
  const [categoryId, setCategoryId] = useState(null)
  const [subCategoryId, setSubCategoryId] = useState(null)
  const [categories, setCategories] = useState([])
  const [subCategoires, setSubCategories] = useState([])
  const [initialValues, setInitalValues] = useState(initial)
  const fetchCategories = async () => {
    await Category.list({
      params: {
        type: 'Medicine',
      },
    })
      .then((resp) => {
        setCategories(resp)
      })
      .catch(() => {})
  }

  const fetchSubCategories = async (parentId) => {
    await Category.list({
      params: {
        parentId,
        type: 'MedicineSubCategory',
      },
    })
      .then((resp) => {
        setSubCategories(resp)
      })
      .catch(() => {})
  }

  const searchHandler = debounce(async (event) => {
    const search = event.target.value
    await Medicine.search({
      params: {
        search,
        categoryId,
        subCategoryId,
      },
    })
      .then(() => {
        setVisible(true)
      })
      .catch(() => {})
  }, 600)

  const handleClick = (event: any) => {
    const name = (event.target as HTMLElement)
      .closest('[data-cross-name]')
      ?.getAttribute('data-cross-name')

    if (!!name) {
      const findIndex = selectedItems?.findIndex(
        (item: any) => item.name == name,
      )
      if (findIndex > -1) {
        setInitalValues(selectedItems[findIndex])
        setMedicineVisible(true)
      } else {
        setInitalValues({
          name: name,
          duration: '',
          consumptions: [],
          notes: '',
        })
        setMedicineVisible(true)
        setVisible(false)
      }
    }
  }

  const submitHandler = async (values) => {
    const findIndex = selectedItems?.findIndex(
      (item: any) => item?.name == name,
    )

    if (findIndex > -1) {
      const updateSelectedItems = [...selectedItems]
      updateSelectedItems[findIndex] = values
      setFieldValue('medicines', updateSelectedItems)
    } else {
      setFieldValue('medicines', [...(selectedItems || []), values])
    }
    setInitalValues(initialValues)
    setMedicineVisible(false)
  }
  useEffect(() => {
    fetchCategories()
  }, [])

  return (
    <Column rowGap={20}>
      <Formik
        initialValues={{
          categoryId: null,
          subCategoryId: null,
        }}
        onSubmit={() => {}}
      >
        {({ setFieldValue }) => {
          return (
            <Column rowGap={15}>
              <Fields.DropDown
                options={categories}
                name="categoryId"
                placeholder="Select Category"
                titleAsKey="name"
                valueAsKey="id"
                label="Select Category"
                onChange={(value) => {
                  setCategoryId(value)
                  setFieldValue('categoryId', value)
                  fetchSubCategories(value)
                }}
              />
              {!!categoryId && (
                <Fields.DropDown
                  options={subCategoires}
                  name="subCategoryId"
                  placeholder="Select Sub Category"
                  titleAsKey="name"
                  valueAsKey="id"
                  label="Select Sub Category"
                  onChange={(value) => {
                    setFieldValue('subCategoryId', value)
                    setSubCategoryId(value)
                  }}
                />
              )}

              {!!categoryId && !!subCategoryId && (
                <StyleWrapper ref={dropdownRef}>
                  <StyleSearch>
                    <StyleInputWrapper>
                      <Svg svg="search" color={'placeholder'} />
                      <StyleInput
                        placeholder={'Search Medicine'}
                        onChange={searchHandler}
                      />
                    </StyleInputWrapper>
                  </StyleSearch>
                  {!!visible && (
                    <>
                      {!!medicines?.length ? (
                        <StyleSearchWrapper onClick={handleClick}>
                          {medicines?.map((medicine: any, index) => {
                            return (
                              <StyleSearchItem
                                key={medicine?.name}
                                data-cross-name={medicine?.name}
                              >
                                <Circle size={25} bg={'inputBorder'}>
                                  <Svg
                                    svg="search"
                                    size={14}
                                    color={'placeholder'}
                                  />
                                </Circle>
                                <Typography
                                  content={medicine?.name}
                                  size={14}
                                  tc="text"
                                />
                              </StyleSearchItem>
                            )
                          })}
                        </StyleSearchWrapper>
                      ) : (
                        <>
                          <Empty />
                          <Row
                            justifyContent="center"
                            styleRow="margin-bottom:30px;"
                          >
                            <Button
                              outline
                              title="Add Medicine"
                              leftComponent={
                                <Svg svg="circlePlus" color={'primary'} />
                              }
                              onClick={() => setMedicineVisible(true)}
                            />
                          </Row>
                        </>
                      )}
                    </>
                  )}
                </StyleWrapper>
              )}
            </Column>
          )
        }}
      </Formik>
      {!!medicineVisible && (
        <Modal
          visible={medicineVisible}
          title="Medicine Details"
          onClose={() => {
            setMedicineVisible(false)
            setInitalValues(initial)
          }}
        >
          <Formik
            initialValues={initialValues}
            onSubmit={() => {}}
            enableReinitialize
            validationSchema={MedicineSchema.addValidationSchema}
          >
            {({ values }) => {
              return (
                <Column rowGap={15}>
                  <Fields.Input
                    name="name"
                    placeholder="Medicine Name"
                    label="Medicine Name"
                  />
                  <Fields.Input
                    name="duration"
                    placeholder="Duration of Medication Use"
                    label="Duration of Medication Use"
                  />
                  <Fields.ChooseButton
                    name="consumptions"
                    label="Consumption Timing"
                    items={[
                      {
                        label: 'Morning',
                      },
                      {
                        label: 'Afternoon',
                      },
                      {
                        label: 'Evening',
                      },
                      {
                        label: 'Before Eating',
                      },
                      {
                        label: 'After Eating',
                      },
                    ]}
                    titleAsKey="label"
                    valueAsKey="label"
                  />
                  <Fields.TextArea
                    name="notes"
                    label="Notes"
                    placeholder="Additional Information"
                  />
                  <Button
                    title="Add Medicine"
                    styleButton="width:100%;"
                    height={40}
                    type="button"
                    onClick={() => {
                      submitHandler(values)
                    }}
                  />
                </Column>
              )
            }}
          </Formik>
        </Modal>
      )}
    </Column>
  )
}

export default SearchMedicines
